import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { Button, Input, Select } from '../../../components/AntComponents';
import { getAllDeviceGroups } from '../../../api/devicegroupService';

const FilterBar = ({ filters, onFiltersChange, onResetFilters, onApplyFilters, streamCount }) => {
  const [deviceGroupOptions, setDeviceGroupOptions] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);

  // Load device groups from API
  useEffect(() => {
    const fetchDeviceGroups = async () => {
      try {
        setLoadingGroups(true);
        const response = await getAllDeviceGroups();
        const groupOptions = response.map(group => ({
          value: group.id.toString(),
          label: group.name
        }));
        
        setDeviceGroupOptions(groupOptions);
      } catch (error) {
        console.error('Failed to load device groups:', error);
      } finally {
        setLoadingGroups(false);
      }
    };

    fetchDeviceGroups();
  }, []);

  const deviceTypeOptions = [
    { value: 'STB', label: 'STB' },
    { value: 'Mobile', label: 'Mobile' },
  ];

  // Updated to match API enums
  const connectionTypeOptions = [
    { value: 'WIFI', label: 'WiFi' },
    { value: 'ETHERNET', label: 'Ethernet' },
    { value: 'SIM', label: 'Cellular' },
  ];

  const connectionStrengthOptions = [
    { value: 'POOR', label: 'Poor' },
    { value: 'FAIR', label: 'Fair' },
    { value: 'GOOD', label: 'Good' },
    { value: 'EXCELLENT', label: 'Excellent' }
  ];

  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const hasActiveFilters = () => {
    // Search term
    if (filters?.searchTerm && filters.searchTerm.trim() !== '') return true;
    
    // ✅ SIMPLIFIED: Device groups (no need to check for 'all' anymore)
    if (Array.isArray(filters?.deviceGroup) && filters.deviceGroup.length > 0) return true;
    
    // Device type (only if it has a valid value)
    if (filters?.deviceType && filters.deviceType !== '' && filters.deviceType !== null && filters.deviceType !== undefined) return true;
    
    // Connection types
    if (Array.isArray(filters?.connectionTypes) && filters.connectionTypes.length > 0) return true;
    
    // Connection strengths
    if (Array.isArray(filters?.connectionStrengths) && filters.connectionStrengths.length > 0) return true;
    
    // Bandwidth filters
    if (filters?.bandwidthMin && filters.bandwidthMin !== '') return true;
    if (filters?.bandwidthMax && filters.bandwidthMax !== '') return true;
    
    // Stream type (excluding 'all' option)
    if (filters?.streamType && filters.streamType !== 'all' && filters.streamType !== '') return true;
    
    return false;
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters?.searchTerm) count++;
    // ✅ SIMPLIFIED: No more 'all' check needed
    if (Array.isArray(filters?.deviceGroup) && filters?.deviceGroup.length > 0) count++;
    if (filters?.deviceType && filters?.deviceType !== '' && filters?.deviceType !== null) count++;
    if (Array.isArray(filters?.connectionTypes) && filters?.connectionTypes.length > 0) count++;
    if (Array.isArray(filters?.connectionStrengths) && filters?.connectionStrengths.length > 0) count++;
    if (filters?.bandwidthMin || filters?.bandwidthMax) count++;
    if (filters?.streamType && filters?.streamType !== 'all') count++;
    return count;
  };

  const filtersActive = hasActiveFilters();
  const activeCount = getActiveFiltersCount();

  return (
    <div className="bg-card border border-border rounded-lg p-4 mb-6">
      {/* Filter Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Button 
            onClick={onResetFilters}
            variant="outline"
          >
            <span>Reset Filters</span>
          </Button>

          <Button 
            type="primary" 
            onClick={onApplyFilters}
            disabled={!filtersActive}
          >
            Apply Filters
          </Button>
        </div>
      </div>

      {/* Grid layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-foreground">
            Search Devices
          </label>
          <Input
            type="search"
            placeholder="Search by name, location, or email"
            value={filters?.searchTerm || ''}
            onChange={(e) => handleFilterChange('searchTerm', e?.target?.value || '')}
            className="w-full"
          />
        </div>

        <div className="space-y-2 overflow-visible">
          <label className="block text-sm font-medium text-foreground">
            Device Groups
          </label>
          <div style={{ position: 'relative', zIndex: 10 }}>
            <Select
              mode="multiple"
              placeholder="Select groups (optional)"
              options={deviceGroupOptions}
              value={filters?.deviceGroup || []}
              onChange={(value) => handleFilterChange('deviceGroup', value || [])}
              allowClear
              maxTagCount="responsive"
              loading={loadingGroups}
              notFoundContent={loadingGroups ? 'Loading...' : 'No groups found'}
              style={{ width: '100%' }}
              dropdownStyle={{ 
                minWidth: '250px',
                maxWidth: '400px',
                zIndex: 1050 
              }}
              dropdownMatchSelectWidth={false}
            />
          </div>
        </div>

        {/* ✅ FIXED: Device Type Filter (No "All" option, can be cleared) */}
        <div className="space-y-2 overflow-visible">
          <label className="block text-sm font-medium text-foreground">
            Device Type
          </label>
          <div style={{ position: 'relative', zIndex: 10 }}>
            <Select
              placeholder="Select device type"
              options={deviceTypeOptions}
              value={filters?.deviceType || undefined}
              onChange={(value) => handleFilterChange('deviceType', value || null)}
              allowClear
              style={{ width: '100%' }}
              dropdownStyle={{ 
                minWidth: '180px',
                maxWidth: '250px',
                zIndex: 1050 
              }}
              dropdownMatchSelectWidth={false}
            />
          </div>
        </div>

        <div className="space-y-2 overflow-visible">
          <label className="block text-sm font-medium text-foreground">
            Connection Type
          </label>
          <div style={{ position: 'relative', zIndex: 10 }}>
            <Select
              mode="multiple"
              placeholder="Select connection type(s)"
              options={connectionTypeOptions}
              value={filters?.connectionTypes || []}
              onChange={(value) => handleFilterChange('connectionTypes', value || [])}
              allowClear
              maxTagCount="responsive"
              style={{ width: '100%' }}
              dropdownStyle={{ 
                minWidth: '180px',
                maxWidth: '300px',
                zIndex: 1050 
              }}
              dropdownMatchSelectWidth={false}
            />
          </div>
        </div>

        {/* <div className="space-y-2 overflow-visible">
          <label className="block text-sm font-medium text-foreground">
            Signal Strength
          </label>
          <div style={{ position: 'relative', zIndex: 10 }}>
            <Select
              mode="multiple"
              placeholder="Signal Strength"
              options={connectionStrengthOptions}
              value={filters?.connectionStrengths || []}
              onChange={(value) => handleFilterChange('connectionStrengths', value || [])}
              allowClear
              maxTagCount="responsive"
              style={{ width: '100%' }}
              dropdownStyle={{ 
                minWidth: '150px',
                maxWidth: '250px',
                zIndex: 1050 
              }}
              dropdownMatchSelectWidth={false}
            />
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default FilterBar;
